import { Howl } from "howler";

export type UiSound = "tap" | "pickup" | "correct" | "incorrect" | "complete";

export const MUSIC_SOURCE = "https://assets.rsxr.eu.cc/nature/media/v1/audio/nature-reply-loop.mp3";
export const MUSIC_LOOP_START_MS = 849;
export const MUSIC_LOOP_END_MS = 147_055;
export const MUSIC_VOLUME = 0.36;
export const MUSIC_DUCKED_VOLUME = 0.225;
export const MUSIC_DUCK_DURATION_MS = 400;
export const MUSIC_RESTORE_DURATION_MS = 650;

interface MusicPlayer {
  play(spriteOrId: "journey" | number): number;
  pause(id: number): void;
  playing(id: number): boolean;
  setVolume(volume: number, id: number): void;
  fade(from: number, to: number, duration: number, id: number): void;
  unload(): void;
}

type MusicPlayerFactory = (onLoadError: () => void) => MusicPlayer;

interface VisibilityHost {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export interface JourneyAudioState {
  started: boolean;
  enabled: boolean;
  ducked: boolean;
  hidden: boolean;
  failed: boolean;
  hasPlayer: boolean;
  soundId: number | null;
}

function createHowlerPlayer(onLoadError: () => void): MusicPlayer {
  const howl = new Howl({
    src: [MUSIC_SOURCE],
    html5: true,
    preload: true,
    autoplay: false,
    volume: MUSIC_VOLUME,
    sprite: {
      journey: [MUSIC_LOOP_START_MS, MUSIC_LOOP_END_MS - MUSIC_LOOP_START_MS, true]
    },
    onloaderror: onLoadError
  });

  return {
    play: (spriteOrId) => howl.play(spriteOrId),
    pause: (id) => howl.pause(id),
    playing: (id) => howl.playing(id),
    setVolume: (volume, id) => {
      howl.volume(volume, id);
    },
    fade: (from, to, duration, id) => {
      howl.fade(from, to, duration, id);
    },
    unload: () => howl.unload()
  };
}

/**
 * Owns the single background-music instance for the whole learning journey.
 * Route changes must call neither dispose nor create another controller.
 */
export class JourneyAudioController {
  private player: MusicPlayer | null = null;
  private soundId: number | null = null;
  private started = false;
  private enabled = true;
  private ducked = false;
  private failed = false;
  private pausedForVisibility = false;
  private currentVolume = MUSIC_VOLUME;
  private listeningForVisibility = false;

  constructor(
    private readonly createPlayer: MusicPlayerFactory = createHowlerPlayer,
    private readonly visibilityHost: VisibilityHost | null = typeof document === "undefined" ? null : document
  ) {}

  start() {
    this.started = true;
    this.listenForVisibility();
    if (this.enabled && !this.isHidden()) this.playOrResume();
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      this.pausedForVisibility = false;
      this.pause();
      return;
    }
    if (this.started && !this.isHidden()) this.playOrResume();
  }

  setDucked(ducked: boolean) {
    if (this.ducked === ducked) return;
    this.ducked = ducked;

    const targetVolume = ducked ? MUSIC_DUCKED_VOLUME : MUSIC_VOLUME;
    const duration = ducked ? MUSIC_DUCK_DURATION_MS : MUSIC_RESTORE_DURATION_MS;
    if (this.player && this.soundId !== null) {
      this.player.fade(this.currentVolume, targetVolume, duration, this.soundId);
    }
    this.currentVolume = targetVolume;
  }

  getState(): JourneyAudioState {
    return {
      started: this.started,
      enabled: this.enabled,
      ducked: this.ducked,
      hidden: this.isHidden(),
      failed: this.failed,
      hasPlayer: this.player !== null,
      soundId: this.soundId
    };
  }

  dispose() {
    if (this.listeningForVisibility && this.visibilityHost) {
      this.visibilityHost.removeEventListener("visibilitychange", this.handleVisibilityChange);
      this.listeningForVisibility = false;
    }
    this.player?.unload();
    this.player = null;
    this.soundId = null;
  }

  private listenForVisibility() {
    if (!this.visibilityHost || this.listeningForVisibility) return;
    this.visibilityHost.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.listeningForVisibility = true;
  }

  private readonly handleVisibilityChange = () => {
    if (this.isHidden()) {
      this.pausedForVisibility = this.started && this.enabled && this.soundId !== null;
      if (this.pausedForVisibility) this.pause();
      return;
    }

    if (this.pausedForVisibility && this.enabled) {
      this.pausedForVisibility = false;
      this.playOrResume();
    }
  };

  private isHidden() {
    return this.visibilityHost?.visibilityState === "hidden";
  }

  private ensurePlayer() {
    if (!this.player && !this.failed) {
      this.player = this.createPlayer(() => {
        this.failed = true;
        this.pause();
      });
    }
    return this.player;
  }

  private playOrResume() {
    if (this.failed || !this.enabled || this.isHidden()) return;
    const player = this.ensurePlayer();
    if (!player) return;

    try {
      if (this.soundId === null) {
        this.soundId = player.play("journey");
        player.setVolume(this.currentVolume, this.soundId);
      } else if (!player.playing(this.soundId)) {
        player.play(this.soundId);
      }
    } catch {
      this.failed = true;
    }
  }

  private pause() {
    if (!this.player || this.soundId === null) return;
    try {
      this.player.pause(this.soundId);
    } catch {
      // Audio failures never block the learning journey.
    }
  }
}

const journeyAudio = new JourneyAudioController();
let audioContext: AudioContext | null = null;
let uiSoundEnabled = true;

const soundNotes: Record<UiSound, Array<{ frequency: number; offset: number; duration: number; volume: number }>> = {
  tap: [{ frequency: 520, offset: 0, duration: 0.045, volume: 0.025 }],
  pickup: [{ frequency: 330, offset: 0, duration: 0.07, volume: 0.035 }],
  correct: [
    { frequency: 523.25, offset: 0, duration: 0.11, volume: 0.045 },
    { frequency: 659.25, offset: 0.07, duration: 0.14, volume: 0.04 }
  ],
  incorrect: [
    { frequency: 246.94, offset: 0, duration: 0.1, volume: 0.035 },
    { frequency: 220, offset: 0.065, duration: 0.13, volume: 0.03 }
  ],
  complete: [
    { frequency: 392, offset: 0, duration: 0.15, volume: 0.04 },
    { frequency: 523.25, offset: 0.09, duration: 0.16, volume: 0.045 },
    { frequency: 659.25, offset: 0.18, duration: 0.2, volume: 0.04 }
  ]
};

function getAudioContext() {
  if (typeof window === "undefined") return null;
  try {
    audioContext ??= new AudioContext();
  } catch {
    return null;
  }
  return audioContext;
}

export function startJourneyAudio() {
  journeyAudio.start();
}

export function setSoundEnabled(enabled: boolean) {
  uiSoundEnabled = enabled;
  journeyAudio.setEnabled(enabled);
}

// Kept as a compatibility alias while chapter components move to the combined controller.
export const setUiSoundEnabled = setSoundEnabled;

export function setMusicDucked(ducked: boolean) {
  journeyAudio.setDucked(ducked);
}

export function getJourneyAudioState() {
  return journeyAudio.getState();
}

// Short interaction cues are synthesized locally so they never interrupt the looping music.
export function playUiSound(sound: UiSound, force = false) {
  if (!uiSoundEnabled && !force) return;
  const context = getAudioContext();
  if (!context) return;
  if (context.state === "suspended") void context.resume();

  const start = context.currentTime + 0.008;
  for (const note of soundNotes[sound]) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const noteStart = start + note.offset;
    const noteEnd = noteStart + note.duration;

    oscillator.type = sound === "incorrect" ? "triangle" : "sine";
    oscillator.frequency.setValueAtTime(note.frequency, noteStart);
    gain.gain.setValueAtTime(0.0001, noteStart);
    gain.gain.exponentialRampToValueAtTime(note.volume, noteStart + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, noteEnd);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(noteStart);
    oscillator.stop(noteEnd + 0.01);
  }
}
